import { padronWarningFromError, spanishArcaConnectError } from '../domain/fiscalPresentation';
import type { ArcaEnvironment, TaxRegime } from '../domain/fiscalTypes';
import { saveFiscalProfileRemote } from '../infra/fiscalRepository';
import { validateArcaSetup } from '../infra/fiscalRepository';
import { consultaPadron } from '../infra/padronAdapter';

export type ConnectArcaInput = {
  branchId: string;
  taxRegime: TaxRegime;
  emitterCuit: string;
  businessName: string;
  domicilioComercial: string;
  iibb: string;
  inicioActividades: string;
  monotributoCategory: string | null;
  environment: ArcaEnvironment;
  certPem?: string;
  privateKeyPem?: string;
  pointOfSale: number;
  regimeChanged?: boolean;
};

export type ConnectArcaResult =
  | { ok: true; lastInvoiceNumber?: number; padronWarning?: string | null }
  | { ok: false; error: string };

/**
 * Una sola conexión: guarda los datos del emisor y el punto de venta, y valida
 * contra ARCA. El certificado viaja en el body de la validación; el servidor
 * lo persiste solo si ARCA responde bien. Así un intento fallido no pisa el
 * certificado que ya estaba guardado.
 */
export async function connectArcaSetup(input: ConnectArcaInput): Promise<ConnectArcaResult> {
  await saveFiscalProfileRemote(
    input.branchId,
    {
      taxRegime: input.taxRegime,
      emitterCuit: input.emitterCuit,
      businessName: input.businessName,
      domicilioComercial: input.domicilioComercial,
      iibb: input.iibb,
      inicioActividades: input.inicioActividades,
      monotributoCategory: input.monotributoCategory,
      environment: input.environment,
      pointOfSale: input.pointOfSale,
    },
    { regimeChanged: input.regimeChanged }
  );

  const result = await validateArcaSetup(input.branchId, undefined, {
    certPem: input.certPem?.trim() || undefined,
    privateKeyPem: input.privateKeyPem?.trim() || undefined,
    emitterCuit: input.emitterCuit,
    pointOfSale: input.pointOfSale,
    taxRegime: input.taxRegime,
    environment: input.environment,
  });

  if (!result.ok) {
    return { ok: false, error: spanishArcaConnectError(result.error) };
  }

  // WSFE puede estar OK y padrón no: son servicios distintos en Clave Fiscal.
  let padronWarning: string | null = null;
  try {
    await consultaPadron(input.emitterCuit.replace(/\D/g, ''), input.branchId);
  } catch (err: unknown) {
    padronWarning = padronWarningFromError(err);
  }

  return { ok: true, lastInvoiceNumber: result.lastInvoiceNumber, padronWarning };
}
